import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category?type=movie" },
  { label: "电视剧", to: "/category?type=tv" },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

interface NavbarProps {
  variant?: "overlay" | "solid";
  currentLabel?: string;
}

export default function Navbar({ variant = "solid", currentLabel }: NavbarProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      const timer = window.setTimeout(() => inputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [searchOpen]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const isOverlay = variant === "overlay" && !scrolled;
  const showSolid = !isOverlay;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
          showSolid ? "bg-background-50/95 backdrop-blur-md border-b border-background-200" : "bg-transparent"
        }`}
      >
        <div className="px-4 md:px-6">
          <div className="h-16 md:h-20 flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2.5 shrink-0 group cursor-pointer">
              <span
                className={`w-9 h-9 rounded-md flex items-center justify-center ${
                  showSolid ? "bg-primary-900 text-accent-400" : "bg-background-50/15 text-accent-300"
                }`}
              >
                <i className="ri-star-smile-line text-lg w-[18px] h-[18px] flex items-center justify-center"></i>
              </span>
              <span className="flex flex-col leading-none">
                <span
                  className={`font-heading text-lg md:text-xl font-semibold tracking-wide ${
                    showSolid ? "text-foreground-950" : "text-background-50"
                  }`}
                >
                  星光传媒
                </span>
                <span
                  className={`text-[10px] tracking-[0.28em] uppercase mt-1 ${
                    showSolid ? "text-foreground-400" : "text-background-200/80"
                  }`}
                >
                  Starlight Media
                </span>
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const active = currentLabel === item.label;
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`px-4 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      active
                        ? showSolid
                          ? "text-primary-700 bg-primary-50 font-medium"
                          : "text-accent-300 bg-background-50/10 font-medium"
                        : showSolid
                          ? "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
                          : "text-background-100/90 hover:text-background-50 hover:bg-background-50/10"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <form onSubmit={submitSearch} className="hidden md:block">
                <div
                  className={`flex items-center gap-2 rounded-md pl-3 pr-1 h-10 w-56 lg:w-64 border transition-colors ${
                    showSolid
                      ? "bg-background-100 border-background-200 focus-within:border-primary-300"
                      : "bg-background-50/10 border-background-50/20 focus-within:border-accent-300"
                  }`}
                >
                  <i
                    className={`ri-search-line text-sm w-4 h-4 flex items-center justify-center ${
                      showSolid ? "text-foreground-400" : "text-background-200"
                    }`}
                  ></i>
                  <input
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索影视作品或资讯"
                    aria-label="全站搜索"
                    className={`flex-1 min-w-0 bg-transparent text-sm outline-none ${
                      showSolid
                        ? "text-foreground-900 placeholder:text-foreground-400"
                        : "text-background-50 placeholder:text-background-200/80"
                    }`}
                  />
                  <button
                    type="submit"
                    className={`h-8 px-3 rounded text-xs cursor-pointer whitespace-nowrap ${
                      showSolid
                        ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                        : "bg-accent-500 text-primary-950 hover:bg-accent-400"
                    }`}
                  >
                    搜索
                  </button>
                </div>
              </form>

              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="打开搜索"
                className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                  showSolid ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"
                }`}
              >
                <i className="ri-search-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                aria-label="打开菜单"
                className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
                  showSolid ? "text-foreground-800 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"
                }`}
              >
                <i className="ri-menu-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setMenuOpen(false)}></div>
        <aside
          className={`absolute top-0 right-0 h-full w-[82%] max-w-sm bg-background-50 flex flex-col transition-transform duration-300 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-16 px-4 flex items-center justify-between border-b border-background-200">
            <span className="font-heading text-lg font-semibold text-foreground-950">星光传媒</span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="关闭菜单"
              className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-close-line text-xl w-6 h-6 flex items-center justify-center"></i>
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-4">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center justify-between px-3 py-3.5 rounded-md text-[15px] cursor-pointer ${
                  currentLabel === item.label
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center text-foreground-300"></i>
              </Link>
            ))}
            <Link
              to="/search"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-between px-3 py-3.5 rounded-md text-[15px] text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              全站搜索
              <i className="ri-search-line text-lg w-5 h-5 flex items-center justify-center text-foreground-300"></i>
            </Link>
          </nav>
          <div className="px-4 py-5 border-t border-background-200">
            <p className="text-xs text-foreground-500 leading-relaxed">
              星光传媒 · 影视作品推荐与资讯
              <br />
              演示数据，仅供内容展示
            </p>
          </div>
        </aside>
      </div>

      {/* Fullscreen search */}
      <div
        className={`fixed inset-0 z-[70] transition-opacity duration-200 ${
          searchOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={() => setSearchOpen(false)}></div>
        <div
          className={`relative bg-background-50 w-full transition-transform duration-300 ${
            searchOpen ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          <form onSubmit={submitSearch} className="px-4 md:px-6 py-5">
            <div className="flex items-center gap-3 mb-4">
              <i className="ri-search-line text-xl text-primary-600 w-6 h-6 flex items-center justify-center"></i>
              <input
                ref={inputRef}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入电影、剧集或资讯关键词"
                aria-label="搜索关键词"
                className="flex-1 min-w-0 bg-transparent text-base outline-none text-foreground-950 placeholder:text-foreground-400"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="关闭搜索"
                className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl w-6 h-6 flex items-center justify-center"></i>
              </button>
            </div>
            <button
              type="submit"
              className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-foreground-400 py-1">热门：</span>
              {["长夜", "悬疑", "星尘列车", "独家采访"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setKeyword(tag)}
                  className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                >
                  {tag}
                </button>
              ))}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}